import {
	StatsigClient,
	StatsigProvider,
	useStatsigUser,
} from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import React from "react";
import { useSelector } from "react-redux";
import { APP_SENTRY_ENV, STATSIG_API_KEY } from "src/config";

// Statsig treats empty object as anonymous user
const ANONYMOUS_USER = Object.freeze({});

const toStatsigUser = (user) => ({
	userID: user.id,
	custom: {
		env: APP_SENTRY_ENV,
		org: user.organisation?.id ?? null,
	},
});

const StatsigBridge = ({ children }) => {
	const { user } = useSelector((state) => state.account);
	const { user: currStatsigUser, updateUserAsync } = useStatsigUser();

	React.useEffect(() => {
		// Prompt Statsig to refetch data on new user by setting user
		// https://docs.statsig.com/client/javascript-sdk/react/#adding-a-user-object

		if (!currStatsigUser.userID && user === null) {
			// no need to set user if no logged in user and statsig user is anonymous.
			return;
		}

		const statsigUser = user !== null ? toStatsigUser(user) : ANONYMOUS_USER;
		updateUserAsync(statsigUser);
	}, [user, currStatsigUser.userID, updateUserAsync]);

	return children;
};

export const WaypointStatsigProvider = ({ children }) => {
	const client = React.useMemo(
		() =>
			new StatsigClient(STATSIG_API_KEY, ANONYMOUS_USER, {
				plugins: [
					new StatsigSessionReplayPlugin(),
					new StatsigAutoCapturePlugin(),
				],
			}),
		[],
	);

	return (
		<StatsigProvider client={client}>
			<StatsigBridge>{children}</StatsigBridge>
		</StatsigProvider>
	);
};
